import { widgetRegistry } from '#features';

import { WidgetActiveCases } from './WidgetActiveCases';
import { WidgetAirFleetTracker } from './WidgetAirFleetTracker';
import { WidgetCrewAvailability } from './WidgetCrewAvailability';
import { WidgetGeoMap } from './WidgetGeoMap';
import { WidgetHubsReport } from './WidgetHubsReport';
import { WidgetNewCases } from './WidgetNewCases';
import { WidgetStatistics } from './WidgetStatistics';
import { WidgetSystemMonitor } from './WidgetSystemMonitor';
import { WidgetWindMap } from './WidgetWindMap';

import type { ControlsProps } from '../shared';
import type { BoxProps } from '@mui/material';
import type { ReactNode } from 'react';

export const initWidgets = () => {
  widgetRegistry.set('active-cases', WidgetActiveCases);
  widgetRegistry.set('airfleet-tracker', WidgetAirFleetTracker);
  widgetRegistry.set('crew-availability', WidgetCrewAvailability);
  widgetRegistry.set('geo-map', WidgetGeoMap);
  widgetRegistry.set('hubs-report', WidgetHubsReport);
  widgetRegistry.set('new-cases', WidgetNewCases);
  widgetRegistry.set('statistics', WidgetStatistics);
  widgetRegistry.set('system-monitor', WidgetSystemMonitor);
  widgetRegistry.set('wind-map', WidgetWindMap);
};

export * from './WidgetActiveCases';
export * from './WidgetAirFleetTracker';
export * from './WidgetCrewAvailability';
export * from './WidgetGeoMap';
export * from './WidgetHubsReport';
export * from './WidgetNewCases';
export * from './WidgetStatistics';
export * from './WidgetSystemMonitor';
export * from './WidgetWindMap';

declare module '#features/widgetManager/widgetRegistry' {
  export interface WidgetRegistry extends Record<WidgetType, unknown> {
    'active-cases': typeof WidgetActiveCases;
    'airfleet-tracker': typeof WidgetAirFleetTracker;
    'crew-availability': typeof WidgetCrewAvailability;
    'geo-map': typeof WidgetGeoMap;
    'hubs-report': typeof WidgetHubsReport;
    'new-cases': typeof WidgetNewCases;
    statistics: typeof WidgetStatistics;
    'system-monitor': typeof WidgetSystemMonitor;
    'wind-map': typeof WidgetWindMap;
  }
  export type WidgetType =
    | 'active-cases'
    | 'airfleet-tracker'
    | 'crew-availability'
    | 'geo-map'
    | 'hubs-report'
    | 'new-cases'
    | 'statistics'
    | 'system-monitor'
    | 'wind-map';

  export type UnknownSettings = Record<string, unknown>;

  export interface WidgetProps<
    TSettings extends Record<string, unknown> = UnknownSettings,
    T extends WidgetType = WidgetType,
    K extends string = keyof TSettings & string,
  > extends Partial<Omit<BoxProps, 'children'>> {
    children?: ((settings: TSettings) => ReactNode) | ReactNode;

    /** Prefer: Block passes instanceId; we'll read settings from Redux */
    instance: WidgetInstance<T>;
    /** Optional schema for settings UI (pure controls map, no legacy keying) */
    config?: Record<K, ControlsProps<K>>;

    showSettingsButton?: boolean;
    outlined?: boolean;
    className?: string;
    style?: React.CSSProperties;
    /** Whether to show a border around the widget */
    showBorder?: boolean;

    /** Material-UI Paper elevation */
    elevation?: number;

    /** Title for the settings modal */
    title?: string;
  }
}
